import { useGetUserProfile } from 'components/account/AccountView/users/hooks/useUsers';
import { useUser } from 'controllers/contexts/user';
import { useEffect, useRef } from 'react';
import { initializeIntercom, shutdownIntercom } from 'utils/intercomUtils';

interface IntercomState {
  previousUserId?: number;
  previousShopId?: number;
  isInitialized: boolean;
  isInitializing: boolean;
  targetUserId?: number;
  targetShopId?: number;
}

const SHUTDOWN_DEBOUNCE_MS = 500;

/**
 * Hook to manage Intercom chat widget initialization and cleanup.
 */
export const useIntercom = (): void => {
  const { user } = useUser();
  const { data: profile, isLoading: isLoadingProfile } = useGetUserProfile();
  const stateRef = useRef<IntercomState>({
    isInitialized: false,
    isInitializing: false,
  });
  const shutdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoadingProfile) {
      return;
    }

    const state = stateRef.current;

    // Clear any pending shutdown when user data arrives
    if (shutdownTimeoutRef.current) {
      clearTimeout(shutdownTimeoutRef.current);
      shutdownTimeoutRef.current = null;
    }

    if (!user?.id) {
      // Debounce shutdown to avoid flickering during data refetches
      if (state.isInitialized || state.isInitializing) {
        shutdownTimeoutRef.current = setTimeout(() => {
          shutdownIntercom();
          stateRef.current = { isInitialized: false, isInitializing: false };
        }, SHUTDOWN_DEBOUNCE_MS);
      }
      return;
    }

    const currentUserId = user.id;
    const currentShopId = user.shop_id;
    const changed =
      state.previousUserId !== currentUserId ||
      state.previousShopId !== currentShopId;

    // Skip if already initializing for the same user/shop
    if (state.isInitializing && !changed) {
      return;
    }

    if (!state.isInitialized || changed) {
      // Shutdown if initialized or initializing (handles mid-init user changes)
      if (state.isInitialized || state.isInitializing) {
        shutdownIntercom();
      }

      state.isInitializing = true;
      state.isInitialized = false;
      state.targetUserId = currentUserId;
      state.targetShopId = currentShopId;

      (async () => {
        const success = await initializeIntercom(user, profile);

        // Only update state if this is still the target user/shop
        if (
          state.targetUserId === currentUserId &&
          state.targetShopId === currentShopId
        ) {
          state.isInitialized = success;
          state.isInitializing = false;
        }
      })();
    }

    state.previousUserId = currentUserId;
    state.previousShopId = currentShopId;
  }, [user, profile, isLoadingProfile]);

  useEffect(() => {
    return () => {
      if (shutdownTimeoutRef.current) {
        clearTimeout(shutdownTimeoutRef.current);
      }
      // Clear target to prevent stale async callbacks
      stateRef.current.targetUserId = undefined;
      stateRef.current.targetShopId = undefined;
      if (stateRef.current.isInitialized || stateRef.current.isInitializing) {
        shutdownIntercom();
      }
    };
  }, []);
};
